import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldCheck, Mail, Lock, User, ArrowRight, Car } from 'lucide-react'

import { ModalShell } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { appRoutes } from '@/constants'

interface AuthModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialMode?: 'login' | 'register'
  title?: string
  description?: string
}

export function AuthModal({
  open,
  onOpenChange,
  initialMode = 'login',
  title,
  description,
}: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const navigate = useNavigate()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onOpenChange(false)
    navigate(appRoutes.dashboard.root)
  }

  return (
    <ModalShell open={open} onOpenChange={onOpenChange} placement="center">
      <div className="p-6 sm:p-8 space-y-6">
        {/* Brand Header */}
        <div className="flex items-center gap-3 border-b border-white/10 pb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/30 text-primary glow-sm">
            <Car className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-white">
              {title || (mode === 'login' ? 'Sign In To Account' : 'Create Free Account')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {description ||
                (mode === 'login'
                  ? 'Access your saved cars, order history, and dealer inquiries.'
                  : 'Join the premier automotive marketplace today.')}
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-white/5 p-1">
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`py-2 text-xs font-semibold rounded-lg transition-colors ${
              mode === 'login' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setMode('register')}
            className={`py-2 text-xs font-semibold rounded-lg transition-colors ${
              mode === 'register' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Register
          </button>
        </div>

        {/* Auth Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">
                Full Name
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/5 border-white/10 text-xs text-white pl-9"
                  required
                />
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">
              Email Address
            </label>
            <div className="relative">
              <Input
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-xs text-white pl-9"
                required
              />
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">
              Password
            </label>
            <div className="relative">
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-xs text-white pl-9"
                required
              />
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          <Button type="submit" size="lg" className="w-full bg-primary text-white hover:bg-primary/90 glow-md mt-2">
            {mode === 'login' ? 'Sign In & Continue' : 'Create Account'} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>

        <div className="pt-2 text-center border-t border-white/10">
          <Link
            to={appRoutes.dashboard.root}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-medium"
          >
            <ShieldCheck className="h-4 w-4 text-primary" /> Are you an authorized dealer? Go to Dealer Portal
          </Link>
        </div>
      </div>
    </ModalShell>
  )
}
